import { canCollect, isFeatureEnabled } from './consent'

export type WireEvent = Record<string, unknown>
export type EnqueueOptions = { anonymous?: boolean }

type QueueItem = { event: WireEvent; anonymous: boolean }

export const MAX_BATCH = 50
export const FLUSH_INTERVAL_MS = 5000
export const MAX_QUEUE = 500
export const MAX_RETRIES = 3

let queue: QueueItem[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let inFlight = false

function endpoint(): string {
  return `${process.env.NEXT_PUBLIC_API_URL ?? ''}/api/v2/events`
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// Anonymous page views need the flag only; everything identifiable needs consent.
function allowed(item: QueueItem): boolean {
  return item.anonymous ? isFeatureEnabled() : canCollect()
}

function stopTimer(): void {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
}

// Consent can be revoked at any moment: drop what may no longer be sent.
function prune(): void {
  queue = queue.filter(item => allowed(item))
  if (queue.length === 0) stopTimer()
}

function clear(): void {
  queue = []
  stopTimer()
}

function schedule(): void {
  if (timer) return
  timer = setTimeout(() => {
    timer = null
    void flush()
  }, FLUSH_INTERVAL_MS)
}

const bodyOf = (items: QueueItem[]) =>
  JSON.stringify({ events: items.map(item => item.event) })

export function enqueue(event: WireEvent, options?: EnqueueOptions): void {
  const item: QueueItem = { event, anonymous: options?.anonymous === true }
  prune()
  if (!allowed(item)) return
  queue.push(item)
  if (queue.length > MAX_QUEUE) {
    queue.splice(0, queue.length - MAX_QUEUE)
  }
  if (queue.length >= MAX_BATCH) {
    void flush()
  } else {
    schedule()
  }
}

async function send(batch: QueueItem[]): Promise<void> {
  let items = batch
  let body = bodyOf(items)
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const kept = items.filter(item => allowed(item))
    if (kept.length !== items.length) {
      items = kept
      body = bodyOf(items)
    }
    if (items.length === 0) {
      prune()
      return
    }
    try {
      const res = await fetch(endpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true
      })
      if (res.ok) return
      if (res.status >= 400 && res.status < 500) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`product analytics batch rejected: ${res.status}`)
        }
        return
      }
    } catch {
      // network error: retry below
    }
    if (attempt < MAX_RETRIES) {
      await sleep(2 ** attempt * 1000)
    }
  }
}

export async function flush(): Promise<void> {
  if (inFlight) return
  prune()
  if (queue.length === 0) return
  const batch = queue.splice(0, MAX_BATCH)
  inFlight = true
  try {
    await send(batch)
  } catch {
    // analytics must never break the app
  } finally {
    inFlight = false
    if (queue.length >= MAX_BATCH) {
      void flush()
    } else if (queue.length > 0) {
      schedule()
    }
  }
}

// Cross-origin beacon cannot send application/json (needs preflight);
// text/plain is CORS-safelisted and the gateway parses JSON regardless.
export function flushOnHide(): void {
  prune()
  if (queue.length === 0) return
  const all = queue.splice(0)
  stopTimer()
  for (let i = 0; i < all.length; i += MAX_BATCH) {
    const body = bodyOf(all.slice(i, i + MAX_BATCH))
    try {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
      const sent =
        typeof navigator !== 'undefined' &&
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(endpoint(), blob)
      if (!sent) {
        void fetch(endpoint(), {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body,
          keepalive: true
        }).catch(() => undefined)
      }
    } catch {
      // best effort on page hide
    }
  }
}

export function resetQueueForTests(): void {
  clear()
  inFlight = false
}
